import { prettifySlug } from './caseStatus';

export type RuleCategory = 'academic' | 'engagement' | 'wellbeing' | 'integrity' | 'other';

export const CATEGORY_LABEL: Record<RuleCategory, string> = {
  academic: 'Academic',
  engagement: 'Engagement',
  wellbeing: 'Wellbeing',
  integrity: 'Integrity',
  other: 'Other',
};

export const CATEGORY_ORDER: RuleCategory[] = ['academic', 'engagement', 'wellbeing', 'integrity', 'other'];

// The 13 rule codes the evaluator ships with, grouped as in the design.
const CATEGORY_BY_CODE: Record<string, Exclude<RuleCategory, 'other'>> = {
  academic_decline_subject: 'academic',
  repeated_chapter_fail: 'academic',
  class_rank_drop: 'academic',
  peer_gap_persistent: 'academic',
  engagement_drop: 'engagement',
  missed_milestone: 'engagement',
  study_plan_skipped: 'engagement',
  streak_break: 'engagement',
  late_night_study: 'engagement',
  wellbeing_red_flag: 'wellbeing',
  wellbeing_silence: 'wellbeing',
  ai_overreliance: 'integrity',
  ai_cheating_suspected: 'integrity',
};

export function categoryOf(code: string): RuleCategory {
  return CATEGORY_BY_CODE[code] ?? 'other';
}

/** `min_streak_days` -> `Min streak days`. No units are invented. */
export function humanizeKey(key: string): string {
  return prettifySlug(key);
}

// Curated label and unit per threshold key the evaluator ships (the design's threshold panel).
// Unknown keys fall back to the humanized key with no unit.
const THRESHOLD_META: Record<string, { label: string; unit?: string }> = {
  samples: { label: 'Exams compared' },
  score_drop_pct: { label: 'Score drop', unit: '%' },
  consecutive_fails: { label: 'Consecutive fails' },
  pass_score_pct: { label: 'Pass score', unit: '%' },
  quartile_threshold: { label: 'Quartile threshold' },
  baseline_weeks: { label: 'Baseline', unit: 'weeks' },
  min_cohort_size: { label: 'Min cohort', unit: 'students' },
  decile_threshold: { label: 'Decile', unit: '%' },
  weeks: { label: 'Weeks' },
  silent_days: { label: 'Silent days', unit: 'days' },
  ratio_threshold: { label: 'Ratio', unit: '× baseline' },
  min_baseline_minutes: { label: 'Min baseline', unit: 'min' },
  skip_threshold: { label: 'Skipped items' },
  min_run_days: { label: 'Min streak', unit: 'days' },
  night_start: { label: 'Night starts', unit: ':00' },
  night_end: { label: 'Night ends', unit: ':00' },
  min_days: { label: 'Min days', unit: 'of 7' },
  consecutive_states: { label: 'Consecutive check-ins' },
  states: { label: 'Red states' },
  baseline_ratio: { label: 'Spike ratio', unit: '× baseline' },
  min_baseline_events: { label: 'Min events' },
  pre_window_minutes: { label: 'Window before', unit: 'min' },
  high_accuracy_pct: { label: 'High accuracy', unit: '%' },
  low_mastery_pct: { label: 'Low mastery', unit: '%' },
};

export const thresholdLabel = (key: string): string => THRESHOLD_META[key]?.label ?? humanizeKey(key);
export const thresholdUnit = (key: string): string | undefined => THRESHOLD_META[key]?.unit;

/** "Grade 10 B" -> "G10 · B" (the design's override summary); other names are left alone. */
export function compactSectionName(display: string): string {
  const m = display.trim().match(/^Grade\s+(\S+)\s+(.+)$/i);
  return m ? `G${m[1]} · ${m[2]}` : display;
}
